import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { Quote } from '../types/myaccount';
import DataTable from './DataTable';
import {accountActionIconGroupStyle, accountActionIconStyle} from './accountActionIconStyles';
import CreateQuoteModal from './CreateQuoteModal';
import MyAccountPageShell from './MyAccountPageShell';
import { PageShellLoading, TableSkeleton, ToolbarSkeleton } from './MyAccountLoadingStates';
import {
    canQuoteBeEdited,
    canQuoteProceedToCheckout,
    getQuoteDisplayId,
    getQuoteDisplayStatus,
    getQuotePrimaryActionTitle,
} from './quoteDisplay';
import { getStatusBadgeClassName } from '../utils/statusBadges';

interface QuoteHistoryProps {
    wekaPagePermissions?: Record<string, boolean>;
}

interface QuoteHistoryLocationState {
    openCreateQuoteModal?: boolean;
}

const QuoteHistory: React.FC<QuoteHistoryProps> = ({ wekaPagePermissions = {} }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<'date_created' | 'quote_number' | 'status' | 'total'>('date_created');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [processingQuoteId, setProcessingQuoteId] = useState<number | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const canCreateQuote = wekaPagePermissions.create_quote !== false;
    const canEditQuote = wekaPagePermissions.edit_quote !== false;
    const canCheckoutQuote = wekaPagePermissions.checkout_quote !== false;

    useEffect(() => {
        loadQuotes();
    }, []);

    useEffect(() => {
        const routeState = location.state as QuoteHistoryLocationState | null;
        if (routeState?.openCreateQuoteModal !== true) {
            return;
        }

        if (canCreateQuote) {
            setIsCreateModalOpen(true);
        }

        navigate(location.pathname, { replace: true, state: null });
    }, [canCreateQuote, location.pathname, location.state, navigate]);

    const loadQuotes = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch('/wp-json/weka/v1/quotes', {
                headers: {
                    'X-WP-Nonce': (window as any).wekaApiSettings?.nonce || '',
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Failed to load quotes');
            }

            const data = await response.json();

            if (data.success) {
                setQuotes(data.quotes || []);
            } else {
                throw new Error(data.error || 'Failed to load quotes');
            }
        } catch (err) {
            console.error('Error loading quotes:', err);
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field: typeof sortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const getSortIcon = (field: typeof sortField) => {
        if (sortField !== field) {
            return <i className="fas fa-sort ms-1 text-muted"></i>;
        }
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up ms-1"></i>
            : <i className="fas fa-sort-down ms-1"></i>;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const formatCurrency = (amount: string | number | undefined) => {
        if (!amount) return '$0.00';
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `$${num.toFixed(2)}`;
    };

    const getStatusLabel = (status: string) => {
        if (status === 'awaiting-payment') {
            return 'Awaiting Payment';
        }

        if (status === 'converted') {
            return 'Converted';
        }

        if (status === 'completed' || status === 'quote-completed') {
            return 'Completed';
        }

        if (status === 'approved') {
            return 'Pending';
        }

        return status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const handlePayQuote = async (quote: Quote) => {
        if (!canCheckoutQuote) {
            alert('Quote checkout is disabled for this page.');
            return;
        }

        if (quote.has_active_checkout_order && quote.converted_order_pay_url) {
            window.location.href = quote.converted_order_pay_url;
            return;
        }

        try {
            setProcessingQuoteId(quote.id);

            const response = await fetch(`/wp-json/weka/v1/quotes/${quote.id}/checkout-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': (window as any).wekaApiSettings?.nonce || '',
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Failed to prepare quote payment');
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Failed to prepare quote payment');
            }

            window.location.href = data.order_pay_url || data.order_view_url || '/my-account/orders';
        } catch (err) {
            console.error('Error preparing quote payment:', err);
            alert(err instanceof Error ? err.message : 'Failed to prepare quote payment');
        } finally {
            setProcessingQuoteId(null);
        }
    };

    // Filter quotes
    const filteredQuotes = quotes.filter(quote => {
        if (!searchTerm) return true;
        
        const search = searchTerm.toLowerCase();
        const quoteNumber = [
            getQuoteDisplayId(quote),
            quote.quote_number || '',
            quote.id.toString(),
        ].join(' ').toLowerCase();
        const title = quote.title.toLowerCase();
        const status = getQuoteDisplayStatus(quote).toLowerCase();
        const total = quote.total?.toString() || '';
        const dateFormatted = formatDate(quote.date_created).toLowerCase();
        const dateISO = quote.date_created.toLowerCase();
        const createdBy = quote.created_by_name?.toLowerCase() || '';
        
        return quoteNumber.includes(search) ||
               title.includes(search) ||
               createdBy.includes(search) ||
               status.includes(search) ||
               total.includes(search) ||
               dateFormatted.includes(search) ||
               dateISO.includes(search);
    });

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) {
        return (
            <PageShellLoading
                className="quote-history"
                showAction={canCreateQuote}
                titleWidth="24%"
                subtitleWidth="48%"
                actionWidth="148px"
            >
                <ToolbarSkeleton />
                <TableSkeleton columns={7} rows={8} />
            </PageShellLoading>
        );
    }

    if (error) {
        return (
            <MyAccountPageShell
                title="Quote History"
                subtitle="View saved quotes and continue quote workflows."
            >
                    <div className="alert alert-danger mb-0">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </div>
            </MyAccountPageShell>
        );
    }

    return (
        <MyAccountPageShell
            className="quote-history"
            title="Quote History"
            subtitle="View saved quotes and continue quote workflows."
            actions={canCreateQuote ? (
                <button
                    type="button"
                    className="btn btn-primary btn-sm weka-primary-btn"
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    <i className="fas fa-plus me-2"></i>
                    Create Quote
                </button>
            ) : undefined}
        >
            {(!canCreateQuote || !canEditQuote || !canCheckoutQuote) && (
                <div className="alert alert-info" role="status">
                    {[
                        !canCreateQuote ? 'Creating quotes is disabled.' : '',
                        !canEditQuote ? 'Editing quotes is disabled.' : '',
                        !canCheckoutQuote ? 'Quote checkout is disabled.' : '',
                    ].filter(Boolean).join(' ')}
                </div>
            )}
            <DataTable
                        rows={filteredQuotes}
                        rowKey={(quote) => quote.id}
                        responsiveMode="stacked-cards"
                        tableClassName="table table-hover"
                        toolbar={{
                            searchValue: searchTerm,
                            onSearchChange: (value) => {
                                setSearchTerm(value);
                                setCurrentPage(1);
                            },
                            searchPlaceholder: 'Search quotes, dates, amounts, status...',
                            entriesPerPage,
                            onEntriesPerPageChange: (value) => {
                                setEntriesPerPage(value);
                                setCurrentPage(1);
                            },
                            filteredCount: filteredQuotes.length,
                            totalCount: quotes.length,
                            countLabel: 'quotes',
                            onClearSearch: () => {
                                setSearchTerm('');
                                setCurrentPage(1);
                            },
                        }}
                        sortable={{
                            field: sortField,
                            direction: sortDirection,
                            clientSide: true,
                            onSort: (field) => handleSort(field as typeof sortField),
                            getSortIcon: (field) => getSortIcon(field as typeof sortField),
                        }}
                        pagination={{
                            currentPage,
                            itemsPerPage: entriesPerPage,
                            totalItems: filteredQuotes.length,
                            itemsLabel: 'quotes',
                            onPageChange: handlePageChange,
                            mode: 'full',
                        }}
                        emptyState={(
                            <div className="alert alert-info mb-0">
                                <i className="fas fa-info-circle me-2"></i>
                                {searchTerm ? 'No quotes found matching your search.' : 'You have no quotes yet.'}
                            </div>
                        )}
                        columns={[
                            {
                                id: 'quote_number',
                                sortable: true,
                                header: 'Quote #',
                                mobileLabel: 'Quote #',
                                mobileDisplay: 'title',
                                sortValue: (quote) => getQuoteDisplayId(quote),
                                render: (quote) => (
                                    <Link to={`/quotes/${quote.id}`} className="weka-account-table-link text-decoration-none">
                                        {getQuoteDisplayId(quote)}
                                    </Link>
                                ),
                            },
                            {
                                id: 'title',
                                header: 'Title',
                                mobileLabel: 'Title',
                                mobileDisplay: 'field',
                                render: (quote) => quote.title,
                            },
                            {
                                id: 'created_by_name',
                                header: 'Created By',
                                mobileLabel: 'Created by',
                                mobileDisplay: 'field',
                                render: (quote) => quote.created_by_name || '—',
                            },
                            {
                                id: 'status',
                                sortable: true,
                                header: 'Status',
                                mobileLabel: 'Status',
                                mobileDisplay: 'field',
                                sortValue: (quote) => getQuoteDisplayStatus(quote),
                                render: (quote) => (
                                    <span className={getStatusBadgeClassName('quotes', getQuoteDisplayStatus(quote))}>
                                        {getStatusLabel(getQuoteDisplayStatus(quote))}
                                    </span>
                                ),
                            },
                            {
                                id: 'date_created',
                                sortable: true,
                                header: 'Date Created',
                                mobileLabel: 'Created',
                                mobileDisplay: 'field',
                                sortValue: (quote) => new Date(quote.date_created).getTime(),
                                render: (quote) => formatDate(quote.date_created),
                            },
                            {
                                id: 'total',
                                sortable: true,
                                header: 'Total',
                                mobileLabel: 'Total',
                                mobileDisplay: 'field',
                                headerClassName: 'text-end',
                                cellClassName: 'text-end',
                                headerStyle: {
                                    minWidth: '8.5rem',
                                    whiteSpace: 'nowrap',
                                    paddingRight: '1.5rem',
                                },
                                cellStyle: {
                                    minWidth: '8.5rem',
                                    whiteSpace: 'nowrap',
                                    paddingRight: '1.5rem',
                                },
                                sortValue: (quote) => parseFloat(quote.total || '0'),
                                render: (quote) => formatCurrency(quote.total),
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                mobileDisplay: 'actions',
                                headerClassName: 'text-center',
                                cellClassName: 'text-center',
                                headerStyle: {
                                    minWidth: '7rem',
                                    whiteSpace: 'nowrap',
                                    paddingLeft: '1rem',
                                },
                                cellStyle: {
                                    minWidth: '7rem',
                                    whiteSpace: 'nowrap',
                                    paddingLeft: '1rem',
                                },
                                render: (quote) => (
                                    <div style={accountActionIconGroupStyle}>
                                        {canCheckoutQuote && canQuoteProceedToCheckout(quote) && (
                                            <button
                                                type="button"
                                                style={accountActionIconStyle}
                                                onClick={() => handlePayQuote(quote)}
                                                disabled={processingQuoteId === quote.id}
                                                title={getQuotePrimaryActionTitle(quote)}
                                                aria-label={getQuotePrimaryActionTitle(quote)}
                                            >
                                                <i className={processingQuoteId === quote.id ? 'fas fa-spinner fa-spin' : 'fas fa-credit-card'}></i>
                                            </button>
                                        )}
                                        {quote.converted_order_view_url && (
                                            <a
                                                href={quote.converted_order_view_url}
                                                title="View order"
                                                aria-label="View order"
                                                style={accountActionIconStyle}
                                            >
                                                <i className="fas fa-receipt"></i>
                                            </a>
                                        )}
                                        {canEditQuote && canQuoteBeEdited(quote) && (
                                            <Link
                                                to={`/quotes/${quote.id}/edit`}
                                                title="Edit quote"
                                                aria-label="Edit quote"
                                                style={accountActionIconStyle}
                                            >
                                                <i className="fas fa-edit"></i>
                                            </Link>
                                        )}
                                    </div>
                                ),
                            },
                        ]}
            />
            <CreateQuoteModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={() => {
                    setCurrentPage(1);
                    void loadQuotes();
                }}
            />
        </MyAccountPageShell>
    );
};

export default QuoteHistory;
